import { BoundSelectField, Button, ButtonMenu, Css, FormLines, HasIdAndName, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { IncrementalCollectionOp } from "src/generated/graphqlTypes";
import { MaterialLeadTimeConfigVariantInput, MaterialLeadTimeFormValue } from "../utils";
import { BoundMaterialLeadTimeConfigFields } from "./BoundMaterialLeadTimeConfigFields";

type BoundMaterialLeadTimeConfigVariantsFieldProps = {
  materialLeadTimeConfig: ObjectState<MaterialLeadTimeFormValue>;
  markets: HasIdAndName[];
};

export function BoundMaterialLeadTimeConfigVariantsField({
  materialLeadTimeConfig,
  markets,
}: BoundMaterialLeadTimeConfigVariantsFieldProps) {
  const { expectedLeadTimeInDays, maxLeadTimeInDays, minLeadTimeInDays, variants } = materialLeadTimeConfig;
  return (
    <Observer>
      {() => (
        <>
          {variants.rows
            .filter(({ op }) => op.value !== IncrementalCollectionOp.Delete)
            .map((formStateVariant, index) => {
              return (
                <BoundMaterialLeadTimeConfigVariant
                  key={`material-lead-time-config-variant-${formStateVariant.id.value ?? index + 1}`}
                  formStateVariant={formStateVariant}
                  markets={markets}
                  materialLeadTimeConfig={materialLeadTimeConfig}
                />
              );
            })}
          <Button
            variant="secondary"
            label="Add Variant"
            icon="plus"
            onClick={() =>
              variants.add({
                op: IncrementalCollectionOp.Include,
                minLeadTimeInDays: minLeadTimeInDays.value,
                expectedLeadTimeInDays: expectedLeadTimeInDays.value,
                maxLeadTimeInDays: maxLeadTimeInDays.value,
              })
            }
            disabled={!variants.valid}
          />
        </>
      )}
    </Observer>
  );
}

type BoundMaterialLeadTimeConfigVariantProps = {
  formStateVariant: ObjectState<MaterialLeadTimeConfigVariantInput>;
  markets: HasIdAndName[];
  materialLeadTimeConfig: ObjectState<MaterialLeadTimeFormValue>;
};

function BoundMaterialLeadTimeConfigVariant({
  formStateVariant,
  markets,
  materialLeadTimeConfig,
}: BoundMaterialLeadTimeConfigVariantProps) {
  const { variants } = materialLeadTimeConfig;
  const selectedMarket = useComputed(
    () => markets.find((market) => market.id === formStateVariant.marketId.value),
    [markets, formStateVariant.marketId],
  );

  const filteredMarkets = useComputed(() => {
    const existingMarketVariants = variants.rows.map((v) => v.marketId.value);
    const currentMarketId = formStateVariant.marketId.value;

    // Filter out all already-used markets (except the one currently selected
    return markets.filter((m) => !existingMarketVariants.includes(m.id) || m.id === currentMarketId).sortByKey("name");
  }, [markets, variants, formStateVariant.marketId]);

  return (
    <div>
      <FormLines>
        <div css={Css.mdSb.df.jcsb.$}>
          <div css={Css.md.mb1.$}>{`Variant ${selectedMarket?.name ?? ""}`}</div>
          <ButtonMenu
            trigger={{ icon: "verticalDots" }}
            items={[
              {
                label: "Delete",
                onClick: () =>
                  formStateVariant.value.id
                    ? formStateVariant.op.set(IncrementalCollectionOp.Delete)
                    : variants.remove(formStateVariant.value),
              },
            ]}
          />
        </div>
        <BoundSelectField
          label="Market"
          labelStyle="hidden"
          field={formStateVariant.marketId}
          options={filteredMarkets}
        />
        <BoundMaterialLeadTimeConfigFields
          minLeadTimeInDays={formStateVariant.minLeadTimeInDays}
          expectedLeadTimeInDays={formStateVariant.expectedLeadTimeInDays}
          maxLeadTimeInDays={formStateVariant.maxLeadTimeInDays}
          reasonNote={formStateVariant.reasonNote}
          disabled={!Boolean(selectedMarket) && "Select a Market"}
        />
      </FormLines>
    </div>
  );
}
